import type { LoggerConfiguration } from '@foundation-player/types';
import { mergeDeep } from '@foundation-player/utils';
import { LogLevel } from './log-level';

/**
 * Catch-All namespace if there was no specific match
 */
export const WILDCARD_NAMESPACE = '*';

export type { LoggerConfiguration };

/**
 * Convert strings to valid logger configurations. The format is:
 * *=WARN,player.core=TRACE,plugin.*=NONE,plugin.advertising=ERROR
 *
 * @param configurationString
 */
export function fromString(configurationString: string): LoggerConfiguration {
  // first parse the string into and array with individual configurations
  // [*=WARN, player.core=TRACE, plugin.*=NONE, plugin.advertising=ERROR]
  // then extract the path and log level, while discarding invalid entries
  // [[*,WARN], [player.core, TRACE], [plugin.*, NONE], [plugin.advertising, ERROR]]
  // [[*,WARN], [[player, core], TRACE], [[plugin,*], NONE], [[plugin,advertising], ERROR]]
  // next write configurations
  // [{'*': LogLevel.Warn}, {player: {core: LogLevel.TRACE}, {plugin: {*: LogLevel.None}, {plugin: {advertising: LogLevel.ERROR}]
  // last merge objects
  // {
  //       *: LogLevel.Warn,
  //       player: {
  //         core: LogLevel.Trace,
  //       },
  //       plugin: {
  //         *: LogLevel.None,
  //         advertising: LogLevel.Error,
  //       }
  //     }

  const logLevelEntries = Object.entries(LogLevel) as [string, LogLevel][];
  const logLevels = logLevelEntries.map(([level, value]) => [level.toLowerCase(), value]);

  return configurationString
    .split(',')
    .reduce(
      (list, part) => {
        if (!part.includes('=')) {
          return list;
        }

        const [segmentString, logLevelString] = part
          .trim()
          .split('=')
          .map((s) => s.trim());
        const segments = segmentString.includes('.') ? segmentString.split('.') : [segmentString];
        const logLevel = logLevels.find(([level]) => level === logLevelString.toLowerCase())?.[1];

        if (typeof logLevel === 'number') {
          list.push([segments, logLevel as LogLevel]);
        }

        return list;
      },
      [] as [string[], LogLevel][],
    )
    .map(([configuration, logLevel]) => {
      const reverse = configuration.reverse();
      let data: LoggerConfiguration = { [reverse[0]]: logLevel };
      for (let i = 1; i < reverse.length; i++) {
        data = { [reverse[i]]: data };
      }

      return data;
    })
    .reduce((finalValue, currentValue) => mergeDeep(finalValue, currentValue), {} as LoggerConfiguration);
}
